import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Download, Repeat, Share2, BookmarkPlus, Copy, Check, Film, Globe } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import NavArrows from "@/components/NavArrows";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtUSD, fmtINR, mediaUrl, kindOf, downloadGen, brand } from "@/lib/api";
import ZoomImage from "@/components/ZoomImage";

export default function Lightbox({ open, onOpenChange, item, onRecreate, onSaveToLibrary, onDeleted, onChanged, nav }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareOn, setShareOn] = useState(!!item?.share_enabled);
  const [published, setPublished] = useState(!!item?.published);
  const [oi, setOi] = useState(0);
  const [maskBusy, setMaskBusy] = useState(false);
  useEffect(() => { setOi(0); }, [item?.id]);

  React.useEffect(() => {
    if (!item) return;
    setShareOn(!!item.share_enabled);
    setPublished(!!item.published);
    if (item.share_enabled && item.share_token) {
      setShareUrl(`${window.location.origin}/share/${item.share_token}`);
    } else {
      setShareUrl("");
    }
    setCopied(false);
  }, [item]);

  if (!item) return null;
  const isVideo = kindOf(item, oi) === "video";
  const isAudio = kindOf(item) === "audio";
  const outs = item.output_urls || [];
  const src = mediaUrl(outs[oi] || outs[0]);
  const outLabel = (i) => item.model_id === "bfx-alphagen" ? ["Alpha matte", "Transparent cutout"][i] || `Output ${i + 1}` : item.mask_applied ? ["Masked result", "Raw generation"][i] || `Output ${i + 1}` : `Output ${i + 1}`;

  const toggleShare = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/generations/${item.id}/share`);
      setShareOn(data.share_enabled);
      if (data.share_enabled) {
        const url = `${window.location.origin}/share/${data.share_token}`;
        setShareUrl(url);
        try { await navigator.clipboard.writeText(url); } catch {}
        toast.success("Share link copied to clipboard");
      } else {
        setShareUrl("");
        toast.success("Share link disabled");
      }
    } catch { toast.error("Could not toggle share"); }
    finally { setSharing(false); }
  };

  const togglePublish = async () => {
    try {
      const { data } = await api.post(`/generations/${item.id}/publish`, { published: !published });
      setPublished(data.published);
      onChanged?.();
      toast.success(data.published ? "Published to the home page" : "Removed from the home page");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change publishing"); }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(()=>setCopied(false), 1800);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(item.prompt || "");
      toast.success("Prompt copied");
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-[#F5F5F7] max-w-[1400px] w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col [&>button:last-child]:hidden" data-testid="lightbox-dialog">
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          {/* Media */}
          <div className="flex-1 min-h-[40vh] lg:min-h-0 min-w-0 bg-black flex items-center justify-center relative">
            {nav && <NavArrows {...nav} testid="lightbox-nav" />}
            {src ? (
              isVideo
                ? <video src={src} controls autoPlay loop playsInline className="absolute inset-0 w-full h-full object-contain"/>
                : isAudio
                  ? <div className="w-full max-w-xl flex flex-col items-center gap-6 p-4">
                      <Film className="w-16 h-16 text-zinc-700"/>
                      <audio src={src} controls className="w-full"/>
                    </div>
                  : <ZoomImage src={src} alt={item.prompt || ""}/>
            ) : <div className="text-zinc-600 font-mono">no output</div>}
          </div>
          {/* Side */}
          <div className="w-full lg:w-[380px] shrink-0 min-h-0 max-h-[50%] lg:max-h-none border-l border-zinc-800 bg-[#0E0E0E] overflow-y-auto p-6 flex flex-col gap-5">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="ax-pill mb-2">{brand(item.model_name)}</div>
                <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{item.category}</div>
              </div>
              <button
                onClick={()=>onOpenChange(false)}
                data-testid="lightbox-close"
                className="h-9 w-9 rounded-full bg-[#1a1a1a] hover:bg-[#222] flex items-center justify-center"
              ><X className="w-4 h-4"/></button>
            </div>

            <div>
              <div className="flex items-center justify-between text-xs font-mono text-zinc-500 uppercase tracking-widest mb-1.5">
                <span>Prompt</span>
                <button onClick={copyPrompt} data-testid="lightbox-copy-prompt" className="text-zinc-500 hover:text-white"><Copy className="w-3.5 h-3.5"/></button>
              </div>
              <p className="text-sm leading-relaxed text-zinc-200 whitespace-pre-wrap">{item.prompt || "—"}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <Row k="Cost" v={<><span className="font-mono">{fmtUSD(item.cost_usd)}</span> <span className="text-zinc-500">·</span> <span className="font-mono">{fmtINR(item.cost_inr)}</span></>}/>
              <Row k="Created" v={new Date(item.created_at).toLocaleString()}/>
              {item.settings?.duration && <Row k="Duration" v={`${item.settings.duration}s`}/>}
              {item.settings?.aspect_ratio && <Row k="Aspect" v={item.settings.aspect_ratio}/>}
              {(() => { const o = (item.output_paths || {})[String(oi)] || {}; return o.w ? <Row k="Output size" v={`${o.w}×${o.h}`}/> : null; })()}
              {item.settings?.resolution && <Row k="Resolution" v={item.settings.resolution}/>}
              {item.settings?.quality && <Row k="Quality" v={item.settings.quality}/>}
            </div>

            <div className="mt-auto space-y-2">
              {<Button
                onClick={()=>onRecreate?.(item)}
                data-testid="lightbox-recreate"
                className="w-full btn-primary-glow text-white font-semibold h-11"
              ><Repeat className="w-4 h-4 mr-2"/> Recreate</Button>}

              {outs.length > 1 && (
                <div className="flex flex-wrap gap-1.5" data-testid="lightbox-outputs">
                  {outs.map((_, i) => (
                    <button key={i} type="button" onClick={()=>setOi(i)} data-testid={`lightbox-output-${i}`}
                      className={`px-3 h-8 rounded-md text-xs border transition-colors ${oi === i ? "border-[#1e97f3] bg-[#0747a1]/40 text-white" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>{outLabel(i)}</button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {src && (
                  <button type="button" onClick={()=>downloadGen(item, oi)}
                     data-testid="lightbox-download"
                     className={`h-10 rounded-md flex items-center justify-center text-sm font-medium bg-[#1a1a1a] border border-zinc-800 hover:bg-[#222]`}>
                    <Download className="w-4 h-4 mr-2"/> {isVideo ? "MP4 (H.264)" : "Download"}
                  </button>
                )}
                {src && isVideo && [["mov", "MOV (ProRes)"], ["png_seq", "PNG sequence"], ["tga_seq", "TGA sequence"],
                  ...(["bfx-cg-render", "bfx-clean-plate", "bfx-action"].includes(item.settings?.bfx_tool) && item.settings?.source_id && oi === 0 ? [["cmp_sbs", "Side by Side"], ["cmp_tb", "Up / Down"]] : [])].map(([f, l]) => (
                  <button key={f} type="button" data-testid={`lightbox-download-${f}`}
                    onClick={() => downloadGen(item, oi, f)}
                    className="h-10 rounded-md flex items-center justify-center text-sm font-medium bg-[#1a1a1a] border border-zinc-800 hover:bg-[#222]">
                    <Download className="w-4 h-4 mr-2"/>{l}</button>
                ))}
                {item.model_id === "bfx-alphagen" && isVideo && item.status === "succeeded" && (
                  <button type="button" data-testid="lightbox-use-as-mask" disabled={maskBusy} onClick={async () => {
                    setMaskBusy(true);
                    try {
                      const { data } = await api.post("/references/from-generation", { generation_id: item.id, index: 0, name: `alpha_matte_${item.id.slice(0, 6)}.mp4` });
                      await api.put("/bfx/cleanplate/session/mask", { project_id: item.project_id, mask: data, alpha_job: null });
                      toast.success("Set as Luma Mask in BFX Cleanup");
                      window.dispatchEvent(new Event("ax-cp-refresh"));
                    } catch (e) { toast.error(errMsg(e, "Could not use this matte")); } finally { setMaskBusy(false); }
                  }} className="col-span-2 h-10 rounded-md flex items-center justify-center text-sm font-medium bfx-gradient-bg text-white disabled:opacity-50">
                    Use as Luma Mask in BFX Cleanup</button>
                )}
                <button
                  onClick={()=>onSaveToLibrary?.(item)}
                  data-testid="lightbox-save-library"
                  className="h-10 rounded-md flex items-center justify-center text-sm font-medium bg-[#1a1a1a] border border-zinc-800 hover:bg-[#222]"
                ><BookmarkPlus className="w-4 h-4 mr-2"/> Save Prompt</button>
              </div>

              <div className="flex items-center justify-between h-11 px-3 rounded-md bg-[#121212] border border-zinc-800" data-testid="lightbox-publish-row">
                <span className="flex items-center gap-2 text-sm"><Globe className={`w-4 h-4 ${published ? "text-[#7AF25F]" : "text-zinc-500"}`} />
                  Publish <span className="text-[11px] text-zinc-500">on home &amp; login page</span></span>
                <Switch checked={published} onCheckedChange={togglePublish} disabled={item.status !== "succeeded"} data-testid="lightbox-publish-toggle" />
              </div>

              <button
                onClick={toggleShare}
                disabled={sharing}
                data-testid="lightbox-share-toggle"
                className={`w-full h-10 rounded-md flex items-center justify-center text-sm font-medium transition-all ${shareOn ? 'bg-[#5FC0C8]/15 border border-[#5FC0C8]/40 text-[#5FC0C8]' : 'bg-[#1a1a1a] border border-zinc-800 hover:bg-[#222]'}`}
              >
                <Share2 className="w-4 h-4 mr-2"/>
                {sharing ? "…" : shareOn ? "Sharing enabled" : "Create share link"}
              </button>

              {shareOn && shareUrl && (
                <div className="flex items-center gap-2 bg-[#121212] border border-zinc-800 rounded-md px-3 h-10" data-testid="lightbox-share-link">
                  <span className="text-xs font-mono truncate flex-1 text-zinc-400">{shareUrl}</span>
                  <button onClick={copyLink} className="text-zinc-400 hover:text-white">
                    {copied ? <Check className="w-4 h-4 text-emerald-400"/> : <Copy className="w-4 h-4"/>}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ k, v }) => (
  <div className="col-span-1">
    <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">{k}</div>
    <div className="text-sm text-zinc-300 mt-0.5">{v}</div>
  </div>
);
